// Peças visuais comuns das abas (marca IntegraIA sobre a estrutura do edvid).
import { useEffect, type ReactNode } from 'react';

export const LABEL = 'text-[10px] font-black uppercase tracking-[0.14em] text-slate-500';
export const BTN = 'rounded-xl border border-white/10 px-3 py-2 text-xs font-black text-slate-200 transition-colors hover:bg-white/10 disabled:opacity-40';
export const BTN_PRIMARY = 'rounded-xl bg-indigo-500 px-4 py-2 text-xs font-black text-white transition-colors hover:bg-indigo-400 disabled:opacity-40';
export const INPUT = 'w-full rounded-xl border border-white/10 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 outline-none focus:border-indigo-500';

export function Panel({ title, right, children, className = '' }: { title?: ReactNode; right?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`rounded-2xl border border-white/10 bg-slate-950/70 p-3 ${className}`}>
      {(title || right) && (
        <div className="mb-2 flex items-center justify-between gap-2">
          {title ? <p className={LABEL}>{title}</p> : <span />}
          {right}
        </div>
      )}
      {children}
    </section>
  );
}

/**
 * Layout das abas com player: vertical (9:16) → player alto à DIREITA e
 * transporte + timeline à esquerda (body.portrait do edvid) em tela larga;
 * horizontal ou tela estreita → empilhado, player em cima.
 */
export function StageLayout({ portrait, player, children }: { portrait: boolean; player: ReactNode; children: ReactNode }) {
  if (portrait) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3 lg:flex-row lg:overflow-hidden">
        <div className="order-2 flex min-h-0 min-w-0 flex-1 flex-col gap-3 lg:order-1 lg:overflow-y-auto lg:pr-1">{children}</div>
        <div className="order-1 flex shrink-0 justify-center lg:order-2 lg:h-full">
          <div className="relative h-[46vh] max-w-[40vw] overflow-hidden rounded-2xl border border-white/10 bg-black lg:h-full lg:max-w-[38vw]" style={{ aspectRatio: '9 / 16' }}>
            {player}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3">
      <div className="flex shrink-0 justify-center">
        <div className="relative h-[42vh] max-w-full overflow-hidden rounded-2xl border border-white/10 bg-black" style={{ aspectRatio: '16 / 9' }}>
          {player}
        </div>
      </div>
      {children}
    </div>
  );
}

export function BusyOverlay({ title, message }: { title: string; message?: string }) {
  return (
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-slate-950/75 p-6 text-center backdrop-blur-[2px]">
      <div className="h-9 w-9 animate-spin rounded-full border-2 border-indigo-400/30 border-t-indigo-400" />
      <p className="text-sm font-black text-white">{title}</p>
      {message && <p className="max-w-xs text-[11px] text-slate-300">{message}</p>}
    </div>
  );
}

/** Atalhos de teclado da aba ativa (ignora campos de texto). */
export function useKeys(handler: (e: KeyboardEvent) => void, enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName) || el.isContentEditable)) return;
      handler(e);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handler, enabled]);
}
